<template>
  <el-dialog
    :model-value="modelValue"
    title="上传到云手机"
    width="min(520px, calc(100vw - 32px))"
    :close-on-click-modal="!isBusy"
    :close-on-press-escape="!isBusy"
    :show-close="!isBusy"
    @update:model-value="updateVisible"
    @closed="resetWhenClosed"
  >
    <section class="upload-dialog">
      <input ref="fileInput" class="file-input" type="file" @change="handleFileChange" />

      <button type="button" class="file-picker" :disabled="isBusy" @click="openFilePicker">
        <el-icon><UploadFilled /></el-icon>
        <span>{{ selectedFile ? selectedFile.name : '选择 APK 或普通文件' }}</span>
      </button>

      <p v-if="selectedFile" class="file-summary">
        {{ formatFileSize(selectedFile.size) }} · {{ isApk ? '上传后安装到云机' : '上传到云机 Download 目录' }}
      </p>

      <el-progress v-if="selectedFile" :percentage="progress" :status="progressStatus" :stroke-width="8" />
      <p v-if="message" class="upload-message" :class="state">{{ message }}</p>
    </section>

    <template #footer>
      <el-button v-if="isBusy" @click="cancelTransfer">取消上传</el-button>
      <el-button v-else @click="updateVisible(false)">{{ isFinished ? '关闭' : '取消' }}</el-button>
      <el-button type="primary" :disabled="!selectedFile || isBusy || isFinished" @click="uploadSelectedFile">
        开始上传
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
/* global defineEmits, defineProps */
import { computed, ref } from 'vue'
import { UploadFilled } from '@element-plus/icons-vue'
import {
  cancelCloudDeviceFileUpload,
  completeCloudDeviceFileUpload,
  fetchCloudDeviceFileUpload,
  initCloudDeviceFileUpload,
  uploadCloudDeviceFileChunk
} from '../../api/cloudDevice'
import { getRequestErrorMessage } from '../../api/http'

const CHUNK_SIZE_BYTES = 8 * 1024 * 1024
const STATUS_POLL_INTERVAL_MILLIS = 2000
const MAX_STATUS_POLL_TIMES = 900

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  deviceId: {
    type: [Number, String],
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'completed'])

const fileInput = ref(null)
const selectedFile = ref(null)
const progress = ref(0)
const state = ref('idle')
const message = ref('')
const uploadId = ref(null)
let abortController = null

const isApk = computed(() => selectedFile.value?.name?.toLowerCase().endsWith('.apk') === true)
const isBusy = computed(() => state.value === 'uploading' || state.value === 'processing')
const isFinished = computed(() => state.value === 'success' || state.value === 'cancelled')
const progressStatus = computed(() => {
  if (state.value === 'success') {
    return 'success'
  }

  return state.value === 'error' ? 'exception' : ''
})

function updateVisible(visible) {
  if (isBusy.value && !visible) {
    return
  }

  emit('update:modelValue', visible)
}

function resetWhenClosed() {
  if (isBusy.value) {
    return
  }

  selectedFile.value = null
  progress.value = 0
  state.value = 'idle'
  message.value = ''
  uploadId.value = null
  abortController = null

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function openFilePicker() {
  fileInput.value?.click()
}

function handleFileChange(event) {
  const file = event.target.files?.[0]
  if (!file) {
    return
  }

  selectedFile.value = file
  progress.value = 0
  state.value = 'idle'
  message.value = ''
  uploadId.value = null
}

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function uploadMessage(action) {
  return action === 'INSTALL' ? '文件已上传，等待云机安装' : '文件已上传，等待云机保存'
}

function successMessage(action) {
  return action === 'INSTALL' ? '已安装到云手机' : '已保存到云机 Download 目录'
}

function delay(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds))
}

async function waitForCloudPhone(upload) {
  for (let attempt = 0; attempt < MAX_STATUS_POLL_TIMES; attempt += 1) {
    if (state.value === 'cancelled') {
      return
    }

    await delay(STATUS_POLL_INTERVAL_MILLIS)

    if (state.value === 'cancelled') {
      return
    }

    const current = await fetchCloudDeviceFileUpload(upload.uploadId)

    if (current.status === 'SUCCESS') {
      state.value = 'success'
      progress.value = 100
      message.value = successMessage(upload.action)
      emit('completed', current)
      return
    }

    if (current.status === 'FAILED' || current.status === 'CANCELED') {
      throw new Error(current.errorMessage || '云机处理文件失败')
    }

    message.value = current.status === 'PROCESSING'
      ? (current.action === 'INSTALL' ? '云机正在安装应用' : '云机正在保存文件')
      : uploadMessage(current.action)
  }

  throw new Error('云机处理文件超时，请稍后在云机内检查')
}

async function uploadSelectedFile() {
  const file = selectedFile.value
  if (!file || isBusy.value) {
    return
  }

  if (file.size <= 0) {
    state.value = 'error'
    message.value = '不能上传空文件'
    return
  }

  abortController = new AbortController()
  state.value = 'uploading'
  message.value = '正在创建上传任务'

  try {
    const upload = await initCloudDeviceFileUpload(props.deviceId, file)
    uploadId.value = upload.uploadId

    let offset = Number(upload.uploadedSize) || 0
    progress.value = Math.floor((offset / file.size) * 100)

    while (offset < file.size) {
      const length = Math.min(CHUNK_SIZE_BYTES, file.size - offset)
      const chunkOffset = offset

      message.value = `正在上传 ${formatFileSize(chunkOffset)} / ${formatFileSize(file.size)}`
      await uploadCloudDeviceFileChunk(
        upload.uploadId,
        file,
        chunkOffset,
        length,
        (event) => {
          const loaded = Math.min(file.size, chunkOffset + (event.loaded || 0))
          progress.value = Math.min(99, Math.floor((loaded / file.size) * 100))
        },
        abortController.signal
      )
      offset += length
      progress.value = Math.min(99, Math.floor((offset / file.size) * 100))
    }

    state.value = 'processing'
    progress.value = 100
    message.value = uploadMessage(upload.action)
    await completeCloudDeviceFileUpload(upload.uploadId)
    await waitForCloudPhone(upload)
  } catch (error) {
    if (error.code === 'ERR_CANCELED') {
      state.value = 'cancelled'
      message.value = '上传已取消'
    } else {
      state.value = 'error'
      message.value = getRequestErrorMessage(error, '上传失败')
    }
  } finally {
    abortController = null
  }
}

async function cancelTransfer() {
  abortController?.abort()

  if (uploadId.value) {
    try {
      await cancelCloudDeviceFileUpload(uploadId.value)
    } catch {
      // The running request may already have been cancelled at the gateway.
    }
  }

  state.value = 'cancelled'
  message.value = '上传已取消'
}
</script>

<style scoped>
.upload-dialog {
  display: grid;
  gap: 14px;
}

.file-input {
  display: none;
}

.file-picker {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 104px;
  padding: 16px;
  overflow: hidden;
  color: #206e61;
  font: inherit;
  background: #eff9f7;
  border: 1px dashed #6ab8a8;
  border-radius: 6px;
  cursor: pointer;
}

.file-picker:disabled {
  cursor: default;
  opacity: 0.65;
}

.file-picker span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-summary,
.upload-message {
  margin: 0;
  color: #66777e;
  font-size: 13px;
}

.upload-message.success {
  color: #178c59;
}

.upload-message.error {
  color: #cf4f4f;
}
</style>
